// Backend · Alerts: one triggered incident as a card, with its diagnostics.

import { Link } from '@tanstack/react-router';
import { Icons } from '@/components/primitives';
import { CPPanel, CPSparkBtn, useExplainPanel } from '@/components/copilot';
import { useCopilotStatus } from '@/api/hooks';
import type { Alert } from '@/api/types';
import { fmtStampEt } from '@/lib/datetime';
import { componentLabel } from '@/lib/chip-state';
import { alertExplainRequest } from '@/lib/copilot';
import { isAdminLogComponent, isAdminLogSignature } from '@/lib/admin-log-search';
import { entityDrawerLink, entityKindLabel, entityRefFromAlert, type EntityKind } from '@/lib/entity-link';
import { CAT_LABEL, KIND_LABEL, SEV_COLOR } from '@/lib/alert-labels';
import { fmtRelative } from './admin-user-drawer';
import { Badge } from './backend-alerts-parts';

/** Turns an entity id into the name an incident shows (fallback: the id). */
export type AlertNameOf = (kind: EntityKind, id: string) => string;

export function alertDisposition(a: Alert): string {
  if (a.status === 'resolved') {
    return `Resolved${a.resolved_by ? ` by ${a.resolved_by}` : ''}${a.resolved_at ? ` · ${fmtStampEt(a.resolved_at)}` : ''}`;
  }
  if (a.status === 'acked') {
    return `Acknowledged${a.acked_by ? ` by ${a.acked_by}` : ''}${a.acked_at ? ` · ${fmtStampEt(a.acked_at)}` : ''}`;
  }
  return a.status;
}

export function alertLogsHref(alert: Alert): string {
  const search = new URLSearchParams({ window: '24h' });
  if (isAdminLogSignature(alert.signature ?? '')) {
    search.set('signature', (alert.signature ?? '').toLowerCase());
  } else if (isAdminLogComponent(alert.component ?? '')) {
    // Alert condition signatures (for example `ttd_intraday_stale`) are not
    // the 16-hex durable-log grouping signature. A component filter is less
    // exact, but remains useful and survives the Logs route validator.
    search.set('component', alert.component ?? '');
  }
  const traceId = alert.diagnostic?.trace_ids?.[0];
  if (traceId) search.set('trace_id', traceId);
  return `/admin/logs?${search.toString()}`;
}

export interface AlertCardProps {
  a: Alert;
  nameOf?: AlertNameOf;
  onAck: () => void;
  onResolve: () => void;
  busy: boolean;
}

export function AlertCard({ a, nameOf, onAck, onResolve, busy }: AlertCardProps) {
  const color = SEV_COLOR[a.severity] ?? 'var(--warn)';
  const done = a.status !== 'active';
  // The entity this incident is about → «Name» · Kind, deep-linking to its drawer.
  const ref = entityRefFromAlert(a);
  const entLink = ref ? entityDrawerLink(ref.kind, ref.id) : null;
  // Copilot §2b — Investigate is a third ghost action on active triggered
  // cards; the ranked-causes panel renders INLINE below the detail text (the
  // card grows — no drawer, no modal). Gated on the server-side flag.
  const copilotOn = useCopilotStatus().data?.enabled === true;
  const ex = useExplainPanel();
  return (
    <div className={done ? 'panel alert-card-done' : 'panel'} style={{ padding: 14, borderLeft: `3px solid ${done ? 'var(--line)' : color}` }}>
      {/* Desktop keeps this row as it was; the classes let the phone restack it
          (`responsive.css`): category and entity as chips, time and state on
          the footer line, the three actions on one equal-width row. */}
      <div className="alert-card-meta" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
        <Badge color={color}>{a.severity}</Badge>
        <span className="mono alert-card-kind" style={{ fontSize: 10.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{KIND_LABEL[a.kind] ?? a.kind}</span>
        {a.category && <span className="alert-card-category" style={{ fontSize: 11, color: 'var(--ink-3)' }}><span className="alert-card-sep">· </span>{CAT_LABEL[a.category] ?? a.category}</span>}
        {ref && (() => {
          const chip = <span className="alert-card-entity" style={{ fontSize: 11, color: 'var(--chart-3)' }}><span className="alert-card-sep">· </span><span className="alert-card-entity-kind">{entityKindLabel(ref.kind)}</span> <span className="alert-card-entity-name">«{nameOf ? nameOf(ref.kind, ref.id) : ref.id}»</span></span>;
          return entLink
            ? <Link to={entLink} preload="intent" className="alert-card-entity-link" style={{ textDecoration: 'none' }} title="Open in its section">{chip}</Link>
            : chip;
        })()}
        <span className="alert-card-actions" style={{ marginLeft: 'auto', display: 'inline-flex', gap: 10, alignItems: 'center' }}>
          {a.count > 1 && <span className="mono alert-card-count" style={{ fontSize: 11 }} title="occurrences">×{a.count.toLocaleString()}</span>}
          <span className="muted alert-card-time" style={{ fontSize: 11 }}>{a.last_seen ? fmtRelative(+new Date(a.last_seen)) : ''}</span>
          {a.status === 'active' ? (
            <>
              <span className="muted mono alert-card-state" style={{ fontSize: 10.5 }}>Active</span>
              {copilotOn && (
                <CPSparkBtn label="Investigate" open={ex.open}
                  onClick={() => (ex.open ? ex.close() : ex.trigger(alertExplainRequest(a.id)))}/>
              )}
              <button className="btn btn-sm" disabled={busy} onClick={onAck}>{Icons.check} Ack</button>
              <button className="btn btn-sm btn-primary" disabled={busy} onClick={onResolve}>Resolve</button>
            </>
          ) : (
            <span className="muted mono" style={{ fontSize: 10.5 }}>{Icons.check} {alertDisposition(a)}</span>
          )}
        </span>
      </div>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{a.title}</div>
      <div className="mono" style={{ fontSize: 12, color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>{a.detail}</div>
      {a.diagnostic && <AlertDiagnosticBlock alert={a} />}
      {copilotOn && ex.open && (
        <div className="cp-dock-in-card">
          <CPPanel resp={ex.resp} state={ex.state} kind="alert" error={ex.error}
            onClose={ex.close} onRegenerate={ex.regenerate} regenBusy={ex.state === 'loading'}/>
        </div>
      )}
      <div className="muted mono alert-card-foot" style={{ fontSize: 10, marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <span className="phone-only">{a.last_seen ? fmtRelative(+new Date(a.last_seen)) : '—'} · {a.status === 'active' ? 'Active' : alertDisposition(a)}</span>
        {a.rule_name && <span>rule: {a.rule_name}</span>}
        <span>first {a.first_seen ? fmtRelative(+new Date(a.first_seen)) : '—'}</span>
        {a.delivered && <span>via {a.delivered}</span>}
      </div>
      {a.status === 'active' && (
        <div className="alert-card-actions-phone phone-only">
          {copilotOn && (
            <CPSparkBtn label="Investigate" open={ex.open}
              onClick={() => (ex.open ? ex.close() : ex.trigger(alertExplainRequest(a.id)))}/>
          )}
          <button className="btn" disabled={busy} onClick={onAck}>{Icons.check} Ack</button>
          <button className="btn btn-primary" disabled={busy} onClick={onResolve}>Resolve</button>
        </div>
      )}
    </div>
  );
}

function AlertDiagnosticBlock({ alert }: { alert: Alert }) {
  const d = alert.diagnostic;
  if (!d) return null;
  const exactLogsHref = alertLogsHref(alert);
  const provider = d.provider;
  const technical = [
    d.environment && ['Environment', d.environment],
    d.schema && ['Schema', d.schema],
    d.routes?.length && ['Route', d.routes.join(', ')],
    d.operations?.length && ['Operation', d.operations.join(', ')],
    d.http_statuses?.length && ['HTTP', d.http_statuses.join(', ')],
    d.error_kinds?.length && ['Error kind', d.error_kinds.join(', ')],
    d.max_latency_ms != null && ['Max latency', `${d.max_latency_ms.toLocaleString()} ms`],
    provider?.provider && ['Provider', componentLabel(provider.provider)],
    provider?.status != null && ['Provider status', String(provider.status)],
    provider?.code && ['Provider code', provider.code],
    d.code_location?.file && ['Code', `${d.code_location.file}${d.code_location.line ? `:${d.code_location.line}` : ''}`],
  ].filter((row): row is [string, string] => Array.isArray(row));

  return (
    <details open style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      <summary style={{ cursor: 'pointer', fontWeight: 650, fontSize: 12 }}>Incident diagnostics</summary>
      <div style={{ marginTop: 9, display: 'grid', gap: 10 }}>
        {(d.root_cause || d.explanation) && (
          <div>
            <div className="field-lbl">Likely cause</div>
            {d.root_cause && <div className="mono" style={{ fontSize: 12, fontWeight: 650 }}>{d.root_cause}</div>}
            {d.explanation && <div style={{ marginTop: 3, fontSize: 12, color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>{d.explanation}</div>}
          </div>
        )}
        {alert.affected_users?.length > 0 && (
          <div>
            <div className="field-lbl">Affected users</div>
            <div style={{ display: 'grid', gap: 4 }}>
              {alert.affected_users.map((user) => (
                <div key={user.id} style={{ fontSize: 12 }}>
                  <strong>{user.name || 'Unknown user'}</strong>
                  {user.email && <span> · {user.email}</span>}
                  <span className="muted mono"> · {user.id} · ×{user.occurrences.toString()}</span>
                </div>
              ))}
              {Number(d.affected_user_count ?? 0) > alert.affected_users.length && (
                <div className="muted" style={{ fontSize: 11 }}>
                  +{Number(d.affected_user_count) - alert.affected_users.length} more affected users
                </div>
              )}
            </div>
          </div>
        )}
        {technical.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '6px 14px' }}>
            {technical.map(([label, value]) => (
              <div key={label} style={{ minWidth: 0 }}>
                <div className="field-lbl">{label}</div>
                <div className="mono" style={{ fontSize: 11, overflowWrap: 'anywhere' }}>{value}</div>
              </div>
            ))}
          </div>
        )}
        {d.evidence?.length > 0 && (
          <div>
            <div className="field-lbl">Correlated timeline</div>
            <div style={{ display: 'grid', gap: 5 }}>
              {d.evidence.slice(0, 5).map((item, index) => (
                <div key={`${item.at ?? ''}-${index}`} className="mono" style={{ fontSize: 11, color: 'var(--ink-2)' }}>
                  {/* fmtStampEt, not toLocaleString: this timeline sits one click
                      from Admin → Logs, which renders the same rows in ET
                      (admin-logs.tsx:590 uses this exact helper). A browser-local
                      time here shows two different clock readings for one event,
                      and carries no zone label to reveal which is which. */}
                  <span className="muted">{item.at ? fmtStampEt(item.at) : 'Unknown time'}</span>
                  {' · '}{item.component ? componentLabel(item.component) : 'system'}{' · '}{item.message}
                </div>
              ))}
            </div>
          </div>
        )}
        {d.suggested_action && (
          <div>
            <div className="field-lbl">Suggested next action</div>
            <div style={{ fontSize: 12 }}>{d.suggested_action}</div>
          </div>
        )}
        <div><Link className="btn btn-sm" to={exactLogsHref} preload="intent">Open exact error group</Link></div>
      </div>
    </details>
  );
}
